import { useState } from "react";
import { faCirclePlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import { isValidContactNumber } from "../Variables";

// Schema-driven replacement for the old upload-lead-form/LeadGForm.js manual "Add New Row" table.
// Renders the always-present common fields (name, phone, provider) plus one input per
// schema-defined field (from the practice's active PracticeSchemaVersion), then submits the same
// {leadsArray, licenseType, leadsAllowed} shape to /api/LeadGenerationUploadLead as before —
// only now each row also carries a CallerInformation object built from the schema fields.
const emptyCallerInfoFor = (schemaFields) =>
  Object.fromEntries(schemaFields.map((f) => [f.name, f.type === "boolean" ? false : ""]));

export const DynamicLeadForm = ({
  providerData,
  schemaFields,
  leadsAllowed,
  onSubmit,
  submitting,
}) => {
  const [rows, setRows] = useState([]);
  const [confirmShow, setConfirmShow] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const addRow = () => {
    if (leadsAllowed !== "" && leadsAllowed <= rows.length) {
      return;
    }
    const firstProvider = providerData[0];
    setRows((current) => [
      ...current,
      {
        localId: Date.now() + Math.random(),
        CallerFirstName: "",
        CallerLastName: "",
        PatientContactNumber: "",
        ProviderId: firstProvider?.Id ?? "",
        ProviderName: firstProvider?.ProviderName ?? "",
        ProviderFacilityName: firstProvider?.ProviderFacilityName ?? "",
        CallerInformation: emptyCallerInfoFor(schemaFields),
      },
    ]);
  };

  const updateRow = (localId, patch) => {
    setRows((current) =>
      current.map((row) => (row.localId === localId ? { ...row, ...patch } : row)),
    );
  };

  const updateCallerInfo = (localId, fieldName, value) => {
    setRows((current) =>
      current.map((row) =>
        row.localId === localId
          ? { ...row, CallerInformation: { ...row.CallerInformation, [fieldName]: value } }
          : row,
      ),
    );
  };

  const removeRow = (localId) => {
    setRows((current) => current.filter((row) => row.localId !== localId));
  };

  const handleProviderChange = (localId, providerId) => {
    const provider = providerData.find((p) => String(p.Id) === String(providerId));
    updateRow(localId, {
      ProviderId: provider?.Id ?? "",
      ProviderName: provider?.ProviderName ?? "",
      ProviderFacilityName: provider?.ProviderFacilityName ?? "",
    });
  };

  const validateRows = () => {
    for (const row of rows) {
      if (
        !row.CallerFirstName.trim() ||
        !row.CallerLastName.trim() ||
        !row.PatientContactNumber.trim()
      ) {
        return "Please enter complete details of all leads";
      }
      if (!isValidContactNumber(row.PatientContactNumber)) {
        return "One or more contact numbers are invalid";
      }
      const missingRequired = schemaFields.filter(
        (f) => f.required && !String(row.CallerInformation[f.name] ?? "").trim(),
      );
      if (missingRequired.length > 0) {
        return `Missing required field(s): ${missingRequired.map((f) => f.name).join(", ")}`;
      }
    }
    return "";
  };

  const handleSaveClick = () => {
    const error = validateRows();
    if (error) {
      setErrorMsg(error);
      return;
    }
    setErrorMsg("");
    setConfirmShow(true);
  };

  const confirmSave = async () => {
    setConfirmShow(false);
    await onSubmit(
      rows.map((row) => {
        const callerPhoneNumber = row.PatientContactNumber.replace(/[-_]/g, "");
        return {
          CallerFirstName: row.CallerFirstName,
          CallerLastName: row.CallerLastName,
          CallerPhoneNumber: callerPhoneNumber,
          ProviderId: row.ProviderId,
          ProviderName: row.ProviderName,
          ProviderFacilityName: row.ProviderFacilityName,
          CallerInformation: row.CallerInformation,
        };
      }),
    );
    setRows([]);
  };

  return (
    <div>
      <h5>Add Leads Manually</h5>
      <div className="d-flex align-items-center mb-3">
        <button className="simple-btn new_Row" onClick={addRow} disabled={submitting}>
          <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
          Add New Row
        </button>
        {errorMsg && <div className="col-md-10 text-center text-danger fs-6">{errorMsg}</div>}
      </div>

      {rows.length > 0 && (
        <>
          <table className="table table-styling mb-3">
            <thead>
              <tr className="text-center table-columns-name">
                <th>First Name</th>
                <th>Last Name</th>
                <th>Contact Number</th>
                <th>Provider</th>
                {schemaFields.map((f) => (
                  <th key={f.name}>
                    {f.name}
                    {f.required && <span className="text-danger"> *</span>}
                  </th>
                ))}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.localId}>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={row.CallerFirstName}
                      onChange={(e) => updateRow(row.localId, { CallerFirstName: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={row.CallerLastName}
                      onChange={(e) => updateRow(row.localId, { CallerLastName: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      maxLength={12}
                      className="form-control"
                      value={row.PatientContactNumber}
                      onChange={(e) =>
                        updateRow(row.localId, { PatientContactNumber: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <select
                      className="form-select"
                      value={row.ProviderId}
                      onChange={(e) => handleProviderChange(row.localId, e.target.value)}
                    >
                      {providerData.map((provider) => (
                        <option key={provider.Id} value={provider.Id}>
                          {provider.ProviderName}
                        </option>
                      ))}
                    </select>
                  </td>
                  {schemaFields.map((f) => (
                    <td key={f.name}>
                      {f.type === "boolean" ? (
                        <input
                          type="checkbox"
                          checked={!!row.CallerInformation[f.name]}
                          onChange={(e) => updateCallerInfo(row.localId, f.name, e.target.checked)}
                        />
                      ) : (
                        <input
                          type={f.type === "date" ? "date" : f.type === "integer" ? "number" : "text"}
                          className="form-control"
                          value={row.CallerInformation[f.name] ?? ""}
                          onChange={(e) => updateCallerInfo(row.localId, f.name, e.target.value)}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <button className="simple-btn" onClick={() => removeRow(row.localId)}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="simple-btn" onClick={handleSaveClick} disabled={submitting}>
            Save
          </button>
        </>
      )}

      <Modal show={confirmShow} onHide={() => setConfirmShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Do you confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>By saving you are verifying that you are uploading the data in the database</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="simple-btn me-2" onClick={() => setConfirmShow(false)}>
            No
          </button>
          <button className="simple-btn" onClick={confirmSave}>
            Yes
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
